/* assets/lab-cart-drawer.js — the cart sheet (DIRECTION.md §4 "Cart sheet", §6 interaction 7).
   Defines <cart-drawer>, the wrapper of <dialog id="CartDrawer">, with the public surface the
   kept product-form.js, cart.js and standard-actions-override.js call: open(), close(),
   renderContents(), getSectionsToRender(), getSectionInnerHTML(), getSectionDOM(),
   setActiveElement(), and the `active` / `is-empty` classes. Also defines <cart-drawer-items>
   (cart.js's CartItems inside the sheet, with the sheet's sections). Replaces Dawn's
   cart-drawer.js, which is never loaded by layout/lab.liquid.

   The dialog is native: showModal() at every width, the top layer, ::backdrop, Escape and
   light dismiss (closedby="any", with a click fallback). Below 640 px an add-to-cart does not
   open the sheet — the phone bottom strip is the receipt — but the strip's Carrello cell
   ([data-cart-open]) and #cart-icon-bubble do. Every listener is delegated to the wrapper or
   the document because each cart mutation replaces the dialog's inner markup.

   The wrapper is also the single owner of the sheet's markup after a mutation: it repaints
   the whole dialog (and, where nothing else does, #cart-icon-bubble) from the Section
   Rendering endpoint, so the sheet can never show a line the cart page has already removed.
   Which paths leave which surface stale is declared once, in STALE_AFTER below. */

(() => {
  'use strict';

  const NARROW = '(width < 640px)';
  const ROW_ADDED_MS = 1200;
  const OPENERS = '#cart-icon-bubble, [data-cart-open]';

  const onCartPage = () => document.querySelector('main[data-template="cart"]') !== null;
  const cartUrl = () => (typeof routes !== 'undefined' && routes && routes.cart_url) || '/cart';
  const always = () => true;
  const never = () => false;

  /* Which surfaces a `cart-update` leaves behind, by the source that published it. This map
     is the single place that records who repaints what after a cart mutation. The three
     surfaces are the sheet (#CartDrawer), the cart icon (#cart-icon-bubble) and the cart
     page's summary column (#main-cart-footer, which holds the shipping field, the total and
     the Checkout button — it sits outside <cart-items>, so nothing in cart.js refreshes it
     unless cart.js itself made the change).

     'cart-items'    cart.js mutated the cart. Inside the sheet it repaints .drawer__inner and
                     #cart-icon-bubble from the /cart/change.js response, and on the cart page
                     it repaints the lines, the icon and the summary — but its subscriber
                     returns early there (assets/cart.js, "if (event.source === 'cart-items')
                     return"), so the sheet keeps the line the customer just removed, with a
                     live Checkout button. Refetch the sheet there, nothing else.
     'product-form'  the add response carries both sections and product-form.js hands them to
                     renderContents() below. On the cart page the gift row is a product-form
                     too, and CartItems refetches only its own lines, so the summary is ours.
     anything else   #onPageShow ('pageshow', a back/forward cache restore or a back/forward
                     load served from the HTTP cache) and
                     standard-actions-override.js ('external-refresh', i.e. apps and the
                     Shopify Standard Actions bundle) and any app publishing cart-update. That
                     path depends on `GET /cart.js?sections=…`, which this storefront answers
                     without a `sections` key, so every surface is refetched here through the
                     Section Rendering endpoint, which does answer. */
  const STALE_AFTER = new Map([
    ['cart-items', { sheet: onCartPage, icon: never, summary: never }],
    ['product-form', { sheet: never, icon: never, summary: onCartPage }],
  ]);
  const STALE_DEFAULT = { sheet: always, icon: always, summary: onCartPage };

  if (!customElements.get('cart-drawer')) {
    class CartDrawer extends HTMLElement {
      #controller = null;
      #unsubscribe = null;
      #rowTimers = new WeakMap();
      #generations = { sheet: 0, icon: 0, summary: 0 };
      activeElement = null;
      productId = null;

      connectedCallback() {
        this.#controller = new AbortController();
        const { signal } = this.#controller;

        this.addEventListener('click', this.#onClick, { signal });
        // `close` does not bubble: capture it on the wrapper so a replaced dialog still reports
        this.addEventListener('close', this.#onClose, { capture: true, signal });
        document.addEventListener('click', this.#onDocumentClick, { signal });
        document.addEventListener('keydown', this.#onDocumentKeydown, { signal });
        document.addEventListener('shopify:section:load', this.#decorateOpeners, { signal });
        window.addEventListener('pageshow', this.#onPageShow, { signal });
        this.#decorateOpeners();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.#unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.#onCartUpdate);
        }
      }

      disconnectedCallback() {
        this.#controller?.abort();
        this.#controller = null;
        if (this.#unsubscribe) this.#unsubscribe();
        this.#unsubscribe = null;
      }

      get dialog() {
        return this.querySelector('#CartDrawer');
      }

      get isOpen() {
        return this.dialog?.open === true;
      }

      /* ---------- public surface ---------- */

      open(triggeredBy) {
        if (triggeredBy) this.setActiveElement(triggeredBy);
        const dialog = this.dialog;
        if (!dialog) return;
        if (!dialog.open) {
          if (typeof dialog.showModal === 'function') dialog.showModal();
          else dialog.setAttribute('open', '');
        }
        this.classList.add('active', 'animate');
        this.#focusInside();
        // cart-drawer-items sets view-event-trigger="manual": the view event fires on open
        this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
      }

      close() {
        const dialog = this.dialog;
        if (dialog?.open) dialog.close();
        else this.#afterClose();
      }

      setActiveElement(element) {
        this.activeElement = element;
      }

      getSectionsToRender() {
        return [
          { id: 'cart-drawer', selector: '#CartDrawer' },
          // The full cart.js shape, so standard-actions-override.js resolves a usable mount
          // for the icon if the `?sections=` API is ever enabled on this storefront. It is
          // not today, which is why #onCartUpdate refetches the icon itself.
          { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
        ];
      }

      getSectionInnerHTML(html, selector = '.shopify-section') {
        const node = this.getSectionDOM(html, selector);
        return node ? node.innerHTML : null;
      }

      getSectionDOM(html, selector = '.shopify-section') {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      }

      renderContents(parsedState) {
        this.classList.remove('is-empty');
        this.querySelector('.drawer__inner')?.classList.remove('is-empty');
        this.productId = parsedState.id;

        for (const entry of this.getSectionsToRender()) {
          // product-form.js asks for the sections by `entry.id`, so that is the response key
          const html = parsedState.sections?.[entry.id];
          if (!html) continue;
          const target = this.#mountFor(entry);
          if (!target) continue;
          const inner = this.getSectionInnerHTML(html, entry.selector);
          if (inner !== null) target.innerHTML = inner;
        }

        this.#markAddedRows(parsedState);

        // below 640 the bottom strip is the receipt; the sheet opens only on request
        if (!window.matchMedia(NARROW).matches) this.open();
        this.announce(this.#addedText(parsedState));
      }

      // Writes a status message where assistive technology can hear it: inside the dialog
      // while it is open (the page around it is inert), outside it while it is closed (a
      // closed dialog is display: none).
      announce(text) {
        if (!text) return;
        const dialog = this.dialog;
        const region = dialog?.open
          ? dialog.querySelector('#CartDrawer-LiveRegionText')
          : this.querySelector('#CartDrawer-Receipt');
        if (!region) return;
        region.setAttribute('aria-hidden', 'false');
        region.textContent = '';
        region.textContent = text;
      }

      /* ---------- internals ---------- */

      // Two entry shapes coexist in Dawn's getSectionsToRender():
      //   { id, selector }           the selector is the mount, found anywhere in the document
      //   { id, section, selector }  the mount is #id (or the `selector` child inside it), and
      //                              `selector` also extracts the fragment from the response
      #mountFor(entry) {
        if (entry.section) {
          const root = document.getElementById(entry.id);
          if (!root) return null;
          return root.querySelector(entry.selector) || root;
        }
        return entry.selector
          ? document.querySelector(entry.selector)
          : document.getElementById(entry.id);
      }

      async #fetchSection(id) {
        const response = await fetch(`${cartUrl()}?section_id=${id}`, {
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) {
          throw new Error(`cart-drawer: section ${id} responded ${response.status}`);
        }
        return response.text();
      }

      // Repaints the whole dialog from the server, so the sheet can never disagree with the
      // cart page: the header count, the empty block, the lines, the shipping field, the
      // total and the Checkout button all come from one render. The token makes the last
      // request win when two mutations overlap.
      #refreshSheet() {
        const token = ++this.#generations.sheet;
        return this.#fetchSection('cart-drawer')
          .then((html) => {
            if (token !== this.#generations.sheet) return;
            const dialog = this.dialog;
            const source = this.getSectionDOM(html, '#CartDrawer');
            if (!dialog) return;
            if (!source) throw new Error('cart-drawer: section cart-drawer has no #CartDrawer');
            const hadFocus = dialog.contains(document.activeElement);
            dialog.innerHTML = source.innerHTML;
            const wrapper = this.getSectionDOM(html, 'cart-drawer');
            if (wrapper) this.classList.toggle('is-empty', wrapper.classList.contains('is-empty'));
            if (dialog.open && hadFocus && !dialog.contains(document.activeElement)) {
              this.#focusInside();
            }
          })
          .catch((error) => {
            console.error(error);
            // The sheet still shows the lines from before this change. Say so in its error
            // slot; the next successful repaint replaces the dialog and clears the message.
            if (token !== this.#generations.sheet) return;
            const slot = this.querySelector('#CartDrawer-CartErrors');
            if (slot) slot.textContent = window.cartStrings?.error || '';
          });
      }

      // The cart page's summary column: the shipping field, the total and the Checkout
      // button. Its section id is the template's key, carried on the element by main-cart-footer.
      #refreshCartSummary() {
        const sectionId = document.getElementById('main-cart-footer')?.dataset.id;
        if (!sectionId) return Promise.resolve();
        const token = ++this.#generations.summary;
        return this.#fetchSection(sectionId)
          .then((html) => {
            if (token !== this.#generations.summary) return;
            // re-queried after the await: another path may have replaced the element
            const footer = document.getElementById('main-cart-footer');
            const contents = footer?.querySelector('.js-contents');
            if (!contents) return;
            const inner = this.getSectionInnerHTML(html, '.js-contents');
            if (inner !== null) contents.innerHTML = inner;
            const source = this.getSectionDOM(html, '#main-cart-footer');
            if (source) footer.classList.toggle('is-empty', source.classList.contains('is-empty'));
            // Checkout sits outside .js-contents, so its disabled state is copied separately
            const freshCheckout = source?.querySelector('#checkout');
            const checkout = document.getElementById('checkout');
            if (freshCheckout && checkout) checkout.disabled = freshCheckout.disabled;
            const errors = document.getElementById('cart-errors');
            if (errors) errors.textContent = '';
          })
          .catch((error) => {
            console.error(error);
            // The total and Checkout still reflect the cart before this change. Say so in the
            // page's error slot; the next successful summary render clears the message.
            if (token !== this.#generations.summary) return;
            const errors = document.getElementById('cart-errors');
            if (errors) errors.textContent = window.cartStrings?.error || '';
          });
      }

      // The cart icon is a single server-rendered section: refetching it keeps the count and
      // its screen-reader sentence correct in every locale without rebuilding strings here.
      #refreshCartIcon() {
        const mount = document.getElementById('cart-icon-bubble');
        if (!mount) return Promise.resolve();
        const token = ++this.#generations.icon;
        return this.#fetchSection('cart-icon-bubble')
          .then((html) => {
            if (token !== this.#generations.icon) return;
            const inner = this.getSectionInnerHTML(html, '.shopify-section');
            if (inner !== null) mount.innerHTML = inner;
          })
          .catch((error) => {
            console.error(error);
          });
      }

      #focusInside() {
        const target = this.querySelector('[data-cart-close]') || this.querySelector('.drawer__inner');
        if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
      }

      #afterClose() {
        if (typeof removeTrapFocus === 'function') removeTrapFocus();
        this.classList.remove('active', 'animate');
        const opener = this.activeElement;
        this.activeElement = null;
        if (opener && opener.isConnected && typeof opener.focus === 'function') {
          opener.focus({ preventScroll: true });
        }
      }

      #onClose = (event) => {
        if (event.target !== this.dialog) return;
        this.#afterClose();
      };

      #onClick = (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        if (target.closest('[data-cart-close]')) {
          event.preventDefault();
          this.close();
          return;
        }
        // light dismiss where closedby="any" is not implemented: a click on the dialog box
        // itself (it has no padding, so only the backdrop area hits it)
        if (target === this.dialog) this.close();
      };

      #onDocumentClick = (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const opener = target?.closest(OPENERS);
        if (!opener || this.contains(opener) || onCartPage()) return;
        event.preventDefault();
        this.open(opener);
      };

      #onDocumentKeydown = (event) => {
        if (event.code !== 'Space') return;
        const target = event.target instanceof Element ? event.target : null;
        const opener = target?.closest('#cart-icon-bubble');
        if (!opener || onCartPage()) return;
        event.preventDefault();
        this.open(opener);
      };

      // #cart-icon-bubble is an <a href="/cart">; wherever the sheet answers it, it is a button
      #decorateOpeners = () => {
        const link = document.getElementById('cart-icon-bubble');
        if (!link || onCartPage()) return;
        link.setAttribute('role', 'button');
        link.setAttribute('aria-haspopup', 'dialog');
      };

      #addedText(parsedState) {
        const variant = parsedState.variant_title && parsedState.variant_title !== 'Default Title'
          ? parsedState.variant_title
          : '';
        return [this.dataset.addedText, parsedState.product_title, variant].filter(Boolean).join(' · ');
      }

      // the added product's rows on the page (per-row forms, format rows) underline for 1.2 s
      #markAddedRows(parsedState) {
        const id = parsedState.variant_id ?? parsedState.id;
        if (!id) return;
        const rows = document.querySelectorAll(`.row[data-variant-id="${id}"]`);
        for (const row of rows) {
          row.classList.add('row--added');
          clearTimeout(this.#rowTimers.get(row));
          this.#rowTimers.set(
            row,
            setTimeout(() => row.classList.remove('row--added'), ROW_ADDED_MS)
          );
        }
      }

      // A page restored from the back/forward cache, or a back/forward load the browser served
      // from its HTTP disk cache (bfcache declined: persisted is false, the navigation type is
      // 'back_forward'), keeps the cart it showed when it was left (checkout return, another
      // tab, an app). Republish the live cart so every surface repaints: 'pageshow' is not in
      // STALE_AFTER, so the sheet, icon and summary refetch.
      #onPageShow = (event) => {
        const backForward = performance.getEntriesByType('navigation')[0]?.type === 'back_forward';
        if ((!event.persisted && !backForward) || typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => {
            if (!response.ok) throw new Error(`cart-drawer: cart.js responded ${response.status}`);
            return response.json();
          })
          .then((cartData) => publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pageshow', cartData }))
          .catch((error) => console.error(error));
      };

      // Every cart mutation lands here. The count is written from the payload; the sheet and
      // the cart icon are refetched only where the path that published the event leaves them
      // behind (STALE_AFTER at the top of this file).
      #onCartUpdate = (event) => {
        const count = event?.cartData?.item_count;
        if (typeof count === 'number') {
          this.dataset.cartCount = String(count);
          // the cart page's header band shows the count; cart.js re-renders only the cart
          // sections, so the number comes from the cart-update payload
          if (onCartPage()) {
            const band = document.querySelector(
              'main[data-template="cart"] .lab-info-band .fact__value .tape'
            );
            if (band && /^\d+$/.test(band.textContent.trim())) band.textContent = String(count);
            // cart.js toggles this class only on its own mutations, and the class lives on
            // the element its refetch does not replace: after an app emptied the cart the
            // page would keep the lines' layout and never show "Il carrello è vuoto."
            document.querySelector('cart-items')?.classList.toggle('is-empty', count === 0);
          }
        }

        const stale = STALE_AFTER.get(event?.source) || STALE_DEFAULT;
        if (stale.sheet()) this.#refreshSheet();
        if (stale.icon()) this.#refreshCartIcon();
        if (stale.summary()) this.#refreshCartSummary();
      };
    }

    customElements.define('cart-drawer', CartDrawer);
  }

  // cart.js declares CartItems at the top level of a classic script; it is loaded first by
  // the layout. Without it there is nothing to extend and quantity changes fall back to the
  // cart page.
  if (typeof CartItems === 'function' && !customElements.get('cart-drawer-items')) {
    class CartDrawerItems extends CartItems {
      getSectionsToRender() {
        return [
          { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
          { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
        ];
      }

      // The sheet's markup has exactly one owner: <cart-drawer>. CartItems' inherited
      // subscriber would refetch the same section here and swap only <cart-drawer-items> and
      // .cart-drawer__footer, leaving the header count and the empty block behind, and it
      // would race the wrapper's refetch. Quantity changes made inside the sheet do not come
      // through here at all: cart.js repaints .drawer__inner from the change response.
      onCartUpdate() {
        return Promise.resolve();
      }
    }

    customElements.define('cart-drawer-items', CartDrawerItems);
  }
})();
